"use client";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  cn,
  formatNumberWithDots,
  generatePaymentCallbackUrl,
} from "@/lib/utils";
import Portal from "../globalPortal";
import {
  IBaseModalProps,
  IDefaultPackage,
  IIncreaseWalletDepositPayload,
  IPackages,
  IUserPackage,
  servicesCategoriesKeys,
  TCreditIncrementSchemaCombinePackage,
  TRawServiceApiKeys,
  TServiceNavigationItem,
} from "@/interfaces";
import { useLockBodyScroll } from "@uidotdev/usehooks";
import {
  useGetInquiry,
  useGetWalletBalance,
  useIncreaseWalletDepositMutation,
  usePurchasePackageMutation,
} from "@/hooks";
import { useSession } from "next-auth/react";
import { usePathname, useSearchParams } from "next/navigation";
import { useAppStore, useSecureStore } from "@/store";
import { usePackages } from "@/context/packagesContext";
import { getClientServiceKeyByAPIFlag } from "@/lib/adapters/serviceFlagAdapter";
import { globalRawServicesData } from "@/constants/globalRawServicesData/globalRawServicesData";
import { useToastStore } from "@/store/toastStore";
import { coreClient } from "@/services";
import { getPackagesAdapter } from "@/lib/adapters/getPackagesAdapter";
import PaymentModalStepOne from "@/components/Payment/paymentModalStepOne";
import PaymentModalStepTwo from "@/components/Payment/paymentModalStepTwo";
import Logo from "@/components/logo";
import styles from "./paymentCheckout.module.css";

interface Props extends IBaseModalProps {
  selectedService?: TServiceNavigationItem;
  paymentSubmission: () => void;
  handleBackBtn?: () => void;
  submissionError: Error | null;
  submissionLoading: boolean;
  showManualError?: string | null;
  persistedData?: Record<string, any>;
  onErrorReset?: () => void;
  payBtnId?: string;
  pageData?: string;
  packages?: IPackages[];
  onPackageSelect?: (packageId: string) => void;
  serviceId?: string | number;
  showData?: "category" | "service";
  allowedCategories?: servicesCategoriesKeys[];
  isTimerActive?: boolean;
  timerEndTime?: number | null;
  onTimerComplete?: () => void;
  serviceManualCost?: number;
  btnTitle?: string;
  paymentNotice?: string;
}

const FALLBACK_DEFAULT_PACKAGE: IDefaultPackage = {
  id: "p0",
  name: "استعلام",
  price: "0",
};

export default function PaymentModalContainer({
  isOpen,
  onClose,
  selectedService,
  paymentSubmission,
  submissionError,
  showManualError = null,
  submissionLoading = false,
  persistedData,
  onErrorReset,
  payBtnId,
  packages,
  onPackageSelect,
  showData = "category",
  serviceId,
  allowedCategories,
  pageData,
  handleBackBtn,
  isTimerActive,
  onTimerComplete,
  timerEndTime,
  serviceManualCost,
  btnTitle,
  paymentNotice,
}: Props) {
  const { packages: globalPackages, user: globalUser, setUser } = usePackages();

  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isPaymentCallbackReturn = searchParams?.get("payment") === "1";
  const isSuccessfulPaymentReturn =
    isPaymentCallbackReturn && searchParams?.get("payment_success") === "1";
  const hasAutoSubmittedRef = useRef(false);
  const { data: session } = useSession();
  const {
    data: walletBalanceData,
    isLoading: isWalletBalanceLoading,
    error: walletBalanceError,
  } = useGetWalletBalance(session?.user.token);
  const {
    mutate: purchasePackageFn,
    isPending: isPurchasing,
    error: purchaseError,
  } = usePurchasePackageMutation();
  const { data: inquiryData, isLoading: isInquiryLoading } = useGetInquiry({
    inquiry: (selectedService?.apiKey as TRawServiceApiKeys) || "",
    token: session?.user.token,
  });
  const isGlobalUserKnown = globalUser !== undefined && globalUser !== null;
  const globalUserIsArray = Array.isArray(globalUser);

  const pathName = usePathname();
  const { setPreviousPath } = useAppStore();
  const { setPersistedFields } = useSecureStore();
  const { setOpenToast } = useToastStore();
  const {
    isPending: isIncreaseSubmitting,
    mutate: increaseWalletDepositFn,
    error: increaseSubmittingError,
    reset: resetIncreaseWalletDeposit,
  } = useIncreaseWalletDepositMutation(session?.user.token);

  const walletBalance = Number(walletBalanceData?.data?.balance || 0);
  const serviceCost = Number(
    serviceManualCost || inquiryData?.data?.price_user || 0,
  );

  const isWalletBalanceInsufficient =
    walletBalance === 0 || walletBalance < serviceCost;

  useLockBodyScroll();

  // Local state
  const [flagPackages, setFlagPackages] = useState<IPackages[] | null>(null);
  const [isUserPackage, setIsUserPackage] = useState(false);
  const [isUseWallet, setIsUseWallet] = useState(false);

  // Memoized values
  const defaultPackage: IDefaultPackage = useMemo(
    () => ({
      id: "p0",
      name: inquiryData?.data?.name || FALLBACK_DEFAULT_PACKAGE.name,
      price: formatNumberWithDots(serviceCost) || String(serviceCost),
    }),
    [inquiryData?.data?.name, serviceCost],
  );

  const allPackages: IPackages[] = useMemo(
    () => flagPackages ?? (packages || globalPackages || []),
    [flagPackages, packages, globalPackages],
  );

  const allowedCategory: servicesCategoriesKeys | null = useMemo(() => {
    const apiKey = selectedService?.apiKey as string | undefined;
    if (!apiKey) return null;
    const enumKey = getClientServiceKeyByAPIFlag(apiKey);
    if (!enumKey) return null;
    return globalRawServicesData[enumKey]?.category ?? null;
  }, [selectedService?.apiKey]);

  // Filter functions
  const filterByServiceId = (items: IPackages[]) => {
    if (!serviceId) return items;
    return items.filter(
      (p) =>
        Array.isArray(p.serviceIds) && p.serviceIds.includes(Number(serviceId)),
    );
  };

  const filterByCategories = (items: IPackages[]) => {
    const categoriesToFilter =
      allowedCategories || (allowedCategory ? [allowedCategory] : null);

    if (!categoriesToFilter || categoriesToFilter.length === 0) return items;

    return items.filter(
      (p) =>
        Array.isArray(p.categories) &&
        p.categories.some((c) => categoriesToFilter.includes(c)),
    );
  };

  const filteredPackages: IPackages[] = useMemo(() => {
    if (showData === "service") {
      return filterByServiceId(allPackages);
    }
    return filterByCategories(allPackages);
  }, [allPackages, showData, serviceId, allowedCategories, allowedCategory]);

  const [selectedPackage, setSelectedPackage] = useState<
    IDefaultPackage | IPackages
  >(defaultPackage);

  // Helper functions
  const isUserOwnedPackage = (pkg: IDefaultPackage | IPackages): boolean => {
    if (!globalUser || !Array.isArray(globalUser) || globalUser.length === 0) {
      return false;
    }

    if ("campaign_id" in pkg && pkg.campaign_id) {
      return globalUser.some(
        (item: IUserPackage) => item.campaign_id === Number(pkg.campaign_id),
      );
    }

    return false;
  };

  const shouldShowStepOne = (): boolean => {
    // Always skip step 1 for postpaid
    if (walletBalanceData?.data?.payment_type === "postpaid") {
      return false;
    }

    // Show step 1 if user has packages to select from
    const hasUserPackages =
      globalUser && Array.isArray(globalUser) && globalUser.length > 0;
    const hasFilteredPackages = filteredPackages && filteredPackages.length > 0;

    return hasUserPackages || hasFilteredPackages;
  };

  // ALL EFFECTS MUST BE HERE - BEFORE ANY RETURN STATEMENTS

  // Fetch packages for the specific service flag when modal opens
  useEffect(() => {
    const apiKey = selectedService?.apiKey as string | undefined;
    if (!isOpen || !apiKey) return;

    let isCancelled = false;
    (async () => {
      try {
        const [packagesRes, inquiriesRes] = await Promise.all([
          coreClient.getPackagesData({
            token: session?.user.token,
            inquiryFlag: apiKey,
          }),
          coreClient.getInquiries(),
        ]);
        const adapted = getPackagesAdapter(
          (packagesRes as any)?.data,
          inquiriesRes?.data,
        );
        if (!isCancelled) {
          setFlagPackages(Array.isArray(adapted.list) ? adapted.list : []);

          if (adapted.user !== undefined) {
            setUser(adapted.user);
          }
        }
      } catch (e) {
        if (!isCancelled) setFlagPackages([]);
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [isOpen, selectedService?.apiKey, session?.user.token, setUser]);

  // Reset errors when modal closes
  useEffect(() => {
    if (!isOpen) {
      resetIncreaseWalletDeposit();
      if (onErrorReset) {
        onErrorReset();
      }
    }
  }, [isOpen, resetIncreaseWalletDeposit, onErrorReset]);

  // Reset errors when component unmounts
  useEffect(() => {
    return () => {
      resetIncreaseWalletDeposit();
      if (onErrorReset) {
        onErrorReset();
      }
    };
  }, [resetIncreaseWalletDeposit, onErrorReset]);

  // Set initial selected package
  useEffect(() => {
    if (isPaymentCallbackReturn) {
      return;
    }

    if (isGlobalUserKnown && globalUserIsArray && globalUser.length > 0) {
      setSelectedPackage(globalUser[0]);
    }
  }, [
    isGlobalUserKnown,
    globalUserIsArray,
    globalUser,
    isPaymentCallbackReturn,
  ]);

  useEffect(() => {
    if (isOpen && isPaymentCallbackReturn) {
      setSelectedPackage(defaultPackage);
      setIsUseWallet(false);
    }
  }, [isOpen, isPaymentCallbackReturn, defaultPackage]);

  useEffect(() => {
    if (
      !isOpen ||
      !isSuccessfulPaymentReturn ||
      isWalletBalanceLoading ||
      isInquiryLoading ||
      hasAutoSubmittedRef.current
    ) {
      return;
    }

    hasAutoSubmittedRef.current = true;
    paymentSubmission();
  }, [
    isOpen,
    isSuccessfulPaymentReturn,
    isWalletBalanceLoading,
    isInquiryLoading,
    paymentSubmission,
  ]);

  // Update isUserPackage state
  useEffect(() => {
    if (isOpen && onPackageSelect && selectedPackage) {
      if ("campaign_id" in selectedPackage && selectedPackage.campaign_id) {
        onPackageSelect(selectedPackage.campaign_id);
      } else if (selectedPackage.id === "p0") {
        onPackageSelect("");
      }
    }

    // Update isUserPackage state
    setIsUserPackage(isUserOwnedPackage(selectedPackage));
  }, [isOpen, selectedPackage, onPackageSelect, globalUser]);

  // Reset selected package if it's not in filtered packages
  useEffect(() => {
    const isUserPackage = (selectedPackage as any).remaining_days !== undefined;
    const isWalletOption = selectedPackage.id === "p0";

    if (
      !isUserPackage &&
      !isWalletOption &&
      "id" in selectedPackage &&
      filteredPackages.length > 0 &&
      !filteredPackages.some((p) => p.id === (selectedPackage as IPackages).id)
    ) {
      setSelectedPackage(defaultPackage);
    }
  }, [filteredPackages, selectedPackage, defaultPackage]);

  // NOW THE EARLY RETURN - AFTER ALL HOOKS
  if (!isOpen || isWalletBalanceLoading || isInquiryLoading) return null;

  // Handler functions
  const handleCreditIncrementSubmission = (
    data: TCreditIncrementSchemaCombinePackage & { is_topup?: boolean },
    bank?: string,
  ) => {
    const payload: IIncreaseWalletDepositPayload = {
      amount: data.amount,
      callback_url: data.callback,
      use_wallet: data.use_wallet,
      items: data.items,
      is_topup: data.is_topup,
    };

    increaseWalletDepositFn(
      { payload, bank },
      {
        onSuccess: (data) => {
          if (data.data) {
            setPreviousPath(pathName);
            if (persistedData) {
              setPersistedFields(persistedData);
            }
            window.location.href = data.data?.payment_link;
          }
        },
      },
    );
  };

  const handlePackageSelect = (pkg: IPackages | IDefaultPackage) => {
    setSelectedPackage(pkg);
  };

  const handleUserPackageSelect = (pkg: IDefaultPackage | any) => {
    setSelectedPackage(pkg);
    if (onPackageSelect) {
      onPackageSelect(pkg.campaign_id);
    }
  };

  const handlePayment = (data?: any) => {
    let shouldClearPersistedFields = true;

    if (data?.topup) {
      handleCreditIncrementSubmission(
        {
          amount: Number(data.digit),
          callback: generatePaymentCallbackUrl(pathname),
          use_wallet: false,
          is_topup: true,
        },
        data.bank,
      );
      return;
    }

    // Check if user selected wallet (p0) or has an existing package
    if (selectedPackage.id === "p0") {
      if (
        !isUseWallet &&
        walletBalanceData?.data?.payment_type !== "postpaid"
      ) {
        const paymentData = {
          amount: isWalletBalanceInsufficient
            ? Number(data?.digit)
            : serviceCost,
          callback: generatePaymentCallbackUrl(pathname),
          use_wallet: false,
        };
        const bank = data?.bank;
        shouldClearPersistedFields = false;
        handleCreditIncrementSubmission(paymentData, bank);
      } else {
        paymentSubmission();
      }
    } else if ((selectedPackage as any).remaining_days) {
      // User selected one of their existing packages - use it for inquiry
      paymentSubmission();
    } else {
      // User selected a new package to purchase
      if (!selectedPackage.price) {
        setOpenToast({
          variant: "default",
          message: "خطا در انتخاب بسته",
        });
        return;
      }

      const numericPrice = selectedPackage.price.replace(/[^\d]/g, "");
      const isWalletGreater = walletBalance >= Number(numericPrice);

      if (!session?.user.token) {
        setOpenToast({
          variant: "default",
          message: "برای خرید بسته ابتدا وارد شوید",
        });
        return;
      }

      if (isUseWallet) {
        if (isWalletGreater) {
          purchasePackageFn(
            {
              purchasedPackageId: Number(selectedPackage?.id),
              token: session?.user.token,
            },
            {
              onSuccess: (res) => {
                setOpenToast({
                  variant: "success",
                  message: "خرید بسته با موفقیت انجام شد",
                });
                onClose();
              },
              onError: (err) => {
                console.error("خطا در خرید پکیج", err);
              },
            },
          );
        } else {
          const finalPrice = Number(numericPrice) - walletBalance;
          const paymentData = {
            amount: Number(finalPrice),
            use_wallet: true,
            callback: generatePaymentCallbackUrl(pathname),
            items: {
              campaign: selectedPackage.id,
            },
          };
          shouldClearPersistedFields = false;
          handleCreditIncrementSubmission(paymentData);
        }
      } else {
        const paymentData = {
          amount: Number(numericPrice),
          use_wallet: false,
          callback: generatePaymentCallbackUrl(pathname),
          items: {
            campaign: selectedPackage.id,
          },
        };
        const bank = data?.bank;
        shouldClearPersistedFields = false;
        handleCreditIncrementSubmission(paymentData, bank);
      }
    }

    setPreviousPath(pathName);
    if (shouldClearPersistedFields) {
      useSecureStore.persist.clearStorage();
    }
  };

  const hasPackageChoices = shouldShowStepOne();
  const paymentSummary = (
    <PaymentModalStepTwo
      onClose={onClose}
      selectedService={selectedService}
      paymentSubmission={(data: any) => handlePayment(data)}
      submissionError={submissionError}
      showManualError={showManualError}
      submissionLoading={submissionLoading}
      payBtnId={payBtnId}
      selectedPackage={selectedPackage}
      globalUser={globalUser}
      isTimerActive={isTimerActive}
      onTimerComplete={onTimerComplete}
      timerEndTime={timerEndTime}
      walletBalanceData={walletBalanceData}
      inquiryData={inquiryData}
      isUseWallet={isUseWallet}
      serviceManualCost={serviceManualCost}
      setIsUseWallet={setIsUseWallet}
      error={walletBalanceError || increaseSubmittingError || submissionError}
      isLoading={isIncreaseSubmitting}
      handleBackBtn={undefined}
      btnTitle={btnTitle}
      paymentNotice={paymentNotice}
    />
  );

  return (
    <Portal>
      <div className={styles.overlay} onMouseDown={onClose}>
        <section
          className={cn(
            styles.checkout,
            !hasPackageChoices && styles.singleCheckout,
          )}
          role="dialog"
          aria-modal="true"
          aria-labelledby="checkout-title"
          onMouseDown={(event) => event.stopPropagation()}
        >
          <header className={styles.header}>
            <div className={styles.headerTitle}>
              <span className={styles.brandMark}>
                <Logo width={35} height={35} />
              </span>
              <div>
                <h2 id="checkout-title">تکمیل سفارش</h2>
                <p>انتخاب بسته و پرداخت</p>
              </div>
            </div>
            <div className={styles.headerActions}>
              <span className={styles.secure}>پرداخت امن</span>
              <button
                className={styles.close}
                type="button"
                aria-label="بستن پنجره پرداخت"
                onClick={onClose}
              >
                <span className={styles.closeGlyph} aria-hidden="true" />
              </button>
            </div>
          </header>
          <div className={styles.body}>
            {hasPackageChoices && (
              <section className={styles.plansPanel}>
                <PaymentModalStepOne
                  selectedService={selectedService}
                  packages={packages}
                  onPackageSelect={onPackageSelect}
                  showData={showData}
                  serviceId={serviceId}
                  allowedCategories={allowedCategories}
                  pageData={pageData}
                  handleBackBtn={handleBackBtn}
                  walletBalanceData={walletBalanceData}
                  inquiryData={inquiryData}
                  handlePackageSelect={handlePackageSelect}
                  handleUserPackageSelect={handleUserPackageSelect}
                  selectedPackage={selectedPackage}
                  onNextStep={() => undefined}
                  serviceManualCost={serviceManualCost}
                  isUserPackage={isUserPackage}
                  hideAction
                />
              </section>
            )}
            <section
              className={cn(
                styles.summaryPanel,
                !hasPackageChoices && styles.singleSummary,
              )}
            >
              {paymentSummary}
            </section>
          </div>
        </section>
      </div>
    </Portal>
  );
}
